import {
    Callout,
    ChoiceGroup,
    DefaultButton,
    DefaultEffects,
    DelayedRender,
    DirectionalHint,
    mergeStyleSets,
    Modal,
    Pivot,
    PivotItem,
    PrimaryButton,
    ScrollablePane,
    Stack,
    Text,
    Toggle
} from '@fluentui/react';
import { useBoolean, useId } from '@fluentui/react-hooks';
import { IC } from 'components/SVGIcon';
import 'intro.js/introjs.css';
import { sendEvent } from 'kits/eventKit';
import { InfoBubble, QuestionCircle } from 'kits/IconKit2';
import IvicosStrings from 'kits/language/stringKit';
import React from 'react';
import { useWindowWidth } from 'shared-state/display-options/hooks';
import 'styles/introjs-custom.css';
import { getClassNames } from 'styles/responsiveness';
import CampusSettingsIntro from './CampusSettings.intro';
import CreateNewOrg from './CreateNewOrg/CreateNewOrg';
import { ICampusSettingsState } from './CampusSettings.state';
import { IntegrationSettings } from './Integrations/IntegrationSettings';
import BrandIdentity from './BrandIdentity/BrandIdentity';
import ExternalDoors from './ExternalDoors/ExternalDoors';

const CampusSettingsView: React.FC<ICampusSettingsState> = (props) => {
    const {
        microsoftProps,
        isFormModalOpen,
        showFormModal,
        hideFormModal,
        titleId,
        campus,
        onCampusFormChange,
        onUpdateCampus,
        changes,
        formFieldsCampus,
        theme,
        fetchCampus,
        pivotDisplaySmallDevice,
        handleLinkClick,
        selectedKey,
        formFieldsVisitors,
        onVisitorFormChange,
        onUpdateVisitor,
        setRemoveOrgOpened,
        removeOrgOpened,
        performActionRemoveOrg,
        removeOrgOptions,
        setRemoveOrgChoice,
        isSavingChanges,
        setIsSavingChanges,
        setChanges,
        dashboardBgImage,
        enabledProviders,
        updateProvider,
        isOrgLoaded,
        isZammadEnabled,
        zammadBaseUrl,
        updateZammadConfig,
        isOpenProjectEnabled,
        openProjectBaseUrl,
        updateOpenProjectConfig,
        isDocumensoEnabled,
        documensoBaseUrl,
        updateDocumensoConfig,
        isZammadTicketEnabled,
        zammadTicketConfiguration,
        updateZammadTicketConfig,
        isColiboEnabled,
        coliboBaseUrl,
        coliboClientId,
        updateColiboConfig
    } = props;

    function _onMsLinkToggle(ev: React.MouseEvent<HTMLElement>, checked?: boolean) {
        if (checked) {
            confirm(IvicosStrings.confirmDescription) === true ? microsoftProps.setMicrosoftTenantIsInvited(true) : !checked;
        }
        return microsoftProps.setMicrosoftTenantIsInvited(false);
    }

    const directionalHint = DirectionalHint.bottomCenter;

    const [isCalloutVisible, { toggle: toggleIsCalloutVisible }] = useBoolean(false);

    const msButtonId = useId('callout-button');

    const styles = mergeStyleSets({
        button: {
            width: 130
        },
        callout: {
            width: 320,
            padding: '20px 24px'
        }
    });

    const { settingsGeneralCard } = getClassNames();
    const { smallDevice, mediumDevice, smallLaptop } = useWindowWidth();

    return (
        <>
            <Stack grow style={{ position: 'relative', height: 'calc( 80vh - 140px )', margin: '0px 30px' }}>
                <ScrollablePane style={{ height: '100%', minHeight: '100%' }}>
                    <Stack horizontal={!smallDevice && !mediumDevice && !smallLaptop} tokens={{ childrenGap: 48 }} verticalAlign="start">
                        <Stack aria-label="brand-identity-component" tokens={{ childrenGap: 16 }} className={`${settingsGeneralCard} display-settings`}>
                            <Stack horizontal horizontalAlign="start" style={{ padding: 2 }} className={pivotDisplaySmallDevice}>
                                <Pivot selectedKey={selectedKey} onLinkClick={handleLinkClick} headersOnly={true} className={`${pivotDisplaySmallDevice} menu`}>
                                    <PivotItem
                                        headerText={IvicosStrings.brandIdentityTitle}
                                        itemKey="campus"
                                        className="one-tab"
                                        style={{ width: '30px', height: '20px' }}
                                    />
                                    <PivotItem headerText={IvicosStrings.externalDoorsTitle} itemKey="external-doors" />
                                    <PivotItem headerText={IvicosStrings.organizationTitle} itemKey="organization" />
                                    <PivotItem headerText={IvicosStrings.integrationsTitle} itemKey="integrations" />
                                </Pivot>
                            </Stack>

                            {selectedKey == 'campus' ? (
                                <BrandIdentity
                                    campusFormFields={formFieldsCampus}
                                    visitorFormFields={formFieldsVisitors}
                                    onCampusFormChange={onCampusFormChange}
                                    onVisitorFormChange={onVisitorFormChange}
                                    dashboardBgImage={dashboardBgImage}
                                />
                            ) : selectedKey == 'integrations' ? (
                                <IntegrationSettings
                                    enabledProviders={enabledProviders}
                                    updateProvider={updateProvider}
                                    isOrgLoaded={isOrgLoaded}
                                    isZammadEnabled={isZammadEnabled}
                                    zammadBaseUrl={zammadBaseUrl}
                                    updateZammadConfig={updateZammadConfig}
                                    isOpenProjectEnabled={isOpenProjectEnabled}
                                    openProjectBaseUrl={openProjectBaseUrl}
                                    updateOpenProjectConfig={updateOpenProjectConfig}
                                    isDocumensoEnabled={isDocumensoEnabled}
                                    documensoBaseUrl={documensoBaseUrl}
                                    updateDocumensoConfig={updateDocumensoConfig}
                                    isZammadTicketEnabled={isZammadTicketEnabled}
                                    zammadTicketConfiguration={zammadTicketConfiguration}
                                    updateZammadTicketConfig={updateZammadTicketConfig}
                                    isColiboEnabled={isColiboEnabled}
                                    coliboBaseUrl={coliboBaseUrl}
                                    coliboClientId={coliboClientId}
                                    updateColiboConfig={updateColiboConfig}
                                />
                            ) : selectedKey == 'organization' ? (
                                <CreateNewOrg setRemoveOrgOpened={setRemoveOrgOpened} />
                            ) : selectedKey == 'external-doors' ? (
                                <ExternalDoors />
                            ) : null}
                            {selectedKey === 'campus' && (
                                <Stack horizontalAlign="end" horizontal style={{ gap: '15px' }}>
                                    <PrimaryButton
                                        text={IvicosStrings.saveActionTitle}
                                        iconProps={{ iconName: 'Save' }}
                                        onClick={() => {
                                            showFormModal();
                                        }}
                                    />
                                </Stack>
                            )}
                        </Stack>
                        {microsoftProps.isMicrosoftUser && (
                            <Stack
                                style={{
                                    width: smallDevice ? '100%' : 380,
                                    background: theme.palette.white,
                                    padding: '16px 26px',
                                    borderRadius: 2,
                                    boxShadow: DefaultEffects.elevation4
                                }}
                            >
                                <Stack horizontal={false} tokens={{ childrenGap: 16 }}>
                                    <Text variant={smallDevice ? 'large' : 'xLarge'} style={{ color: theme.palette.black }}>
                                        {IvicosStrings.accessMethodTitle}
                                    </Text>
                                    {microsoftProps.isMicrosoftUser && (
                                        <Stack>
                                            <Stack horizontal horizontalAlign="space-around">
                                                <Toggle onChange={_onMsLinkToggle} checked={microsoftProps.microsoftTenantIsInvited} />
                                                <Stack style={{ width: '100%', paddingLeft: '10px' }}>
                                                    {microsoftProps.microsoftTenantIsInvited ? (
                                                        <Text
                                                            variant="medium"
                                                            style={
                                                                smallDevice
                                                                    ? { width: '100%', color: theme.palette.black }
                                                                    : { width: '75%', color: theme.palette.black }
                                                            }
                                                        >
                                                            {IvicosStrings.switchOffDescription}
                                                        </Text>
                                                    ) : (
                                                        <Text
                                                            variant="medium"
                                                            style={
                                                                smallDevice
                                                                    ? { width: '100%', color: theme.palette.black }
                                                                    : { width: '70%', color: theme.palette.black }
                                                            }
                                                        >
                                                            {IvicosStrings.switchOnDescription}
                                                        </Text>
                                                    )}
                                                </Stack>
                                                {!smallDevice && (
                                                    <Stack verticalAlign="center" style={{ width: '20px' }}>
                                                        <IC variant="custom" size={15} style={{ cursor: 'pointer' }}>
                                                            <QuestionCircle onClick={toggleIsCalloutVisible} id={msButtonId} />
                                                        </IC>
                                                    </Stack>
                                                )}
                                                {isCalloutVisible && (
                                                    <Callout
                                                        className={styles.callout}
                                                        onDismiss={toggleIsCalloutVisible}
                                                        role="status"
                                                        target={`#${msButtonId}`}
                                                        aria-live="assertive"
                                                        directionalHint={directionalHint}
                                                    >
                                                        <DelayedRender>
                                                            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="start">
                                                                <IC size={24} variant="dark">
                                                                    <InfoBubble />
                                                                </IC>
                                                                <Stack>
                                                                    <Text variant="medium" style={{ marginBottom: 5 }}>
                                                                        {IvicosStrings.accessMethodCallout1}{' '}
                                                                    </Text>
                                                                    <Text variant="medium">{IvicosStrings.accessMethodCallout2}</Text>
                                                                </Stack>
                                                            </Stack>
                                                        </DelayedRender>
                                                    </Callout>
                                                )}
                                            </Stack>
                                        </Stack>
                                    )}
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </ScrollablePane>
            </Stack>
            <CampusSettingsIntro />

            <Modal isOpen={isFormModalOpen} onDismiss={hideFormModal} isBlocking={false} titleAriaId={titleId}>
                <>
                    <Stack
                        tokens={{ childrenGap: 17 }}
                        style={{ justifyContent: 'center', alignContent: 'center', padding: '20px', width: '330px', height: '110px' }}
                    >
                        <Text variant="xLarge">{IvicosStrings.saveChangesActionTitle}?</Text>
                        <Text>{IvicosStrings.saveCampusSettingsDescription}</Text>
                    </Stack>
                    <Stack horizontal verticalAlign="center" horizontalAlign="end" tokens={{ childrenGap: 7 }} style={{ padding: '20px' }}>
                        {!isSavingChanges && (
                            <DefaultButton
                                text={IvicosStrings.cancelActionTitle}
                                onClick={() => {
                                    hideFormModal();
                                    setIsSavingChanges(false);
                                }}
                            />
                        )}
                        <PrimaryButton
                            aria-label="btn-to-save-changes-in-settings"
                            text={isSavingChanges ? IvicosStrings.isSavingChanges : IvicosStrings.confirmActionTitle}
                            onClick={async () => {
                                setIsSavingChanges(true);
                                if (!campus) return;
                                if (changes && Object.keys(changes).length > 0) {
                                    const visitorChangeKeys = ['image', 'video-url', 'message'];
                                    const hasVisitorChanges = visitorChangeKeys.some((key) => Object.prototype.hasOwnProperty.call(changes, key));
                                    const hasCampusChanges = Object.keys(changes).some((key) => !visitorChangeKeys.includes(key));

                                    let response;
                                    if (hasVisitorChanges && hasCampusChanges) {
                                        const visitorResponse = await (onUpdateVisitor && onUpdateVisitor(changes));
                                        const campusResponse = await (onUpdateCampus && onUpdateCampus(campus.orgId, changes));
                                        response = visitorResponse || campusResponse;
                                    } else if (hasCampusChanges) {
                                        response = await (onUpdateCampus && onUpdateCampus(campus.orgId, changes));
                                    } else {
                                        response = await (onUpdateVisitor && onUpdateVisitor(changes));
                                    }
                                    if (response) {
                                        setTimeout(() => {
                                            sendEvent('refetchBranding', {});
                                            fetchCampus();
                                            setChanges({});
                                            hideFormModal();
                                        }, 1000);
                                        setTimeout(() => {
                                            setIsSavingChanges(false);
                                        }, 1200);
                                    }

                                    return;
                                } else {
                                    setIsSavingChanges(false);
                                    hideFormModal();
                                    return;
                                }
                            }}
                        />
                    </Stack>
                </>
            </Modal>
            <Modal isBlocking isOpen={removeOrgOpened} onDismiss={() => setRemoveOrgOpened(false)}>
                <Stack
                    horizontalAlign="center"
                    style={{ maxWidth: 400, padding: 26, alignItems: 'start' }}
                    tokens={{ childrenGap: 32 }}
                    aria-label="remove-org-modal"
                >
                    <Stack horizontalAlign="center" tokens={{ childrenGap: 6 }} style={{ alignItems: 'start', gap: '20px' }}>
                        <Text variant="xLarge">{IvicosStrings.removeOrganizationModalTitle}</Text>
                        <Text>{IvicosStrings.removeOrganizationQuestion.replace('[org]', campus ? campus.name : 'this organisation')}</Text>
                        <Text>{IvicosStrings.removeOrganizationExplanation}</Text>
                        <ChoiceGroup
                            defaultSelectedKey={'refuse'}
                            options={removeOrgOptions}
                            onChange={(e, option) => {
                                e?.preventDefault;
                                setRemoveOrgChoice(option?.key);
                            }}
                            label={IvicosStrings.trialPeriodExpiredChoiceTitle}
                            aria-label="remove-org-choices"
                            required
                        />
                    </Stack>
                    <PrimaryButton
                        text={IvicosStrings.okActionTitle}
                        onClick={performActionRemoveOrg}
                        style={{ alignSelf: 'end' }}
                        aria-label="remove-org-ok-button"
                    />
                </Stack>
            </Modal>
        </>
    );
};

export default CampusSettingsView;
